import type { Href } from 'expo-router';
import { useRouter } from 'expo-router';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { PartnerSubscriptionStatus } from '@/api/partners/subscriptions/types';
import { Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Dot } from '@/ui/icons/dot';

import {
  getPartnerSubscriptionStatusChipType,
  getPartnerSubscriptionStatusLabel,
} from './status-label';

/** Underlined ID that navigates to `href`, used in table cells. */
export const IdLink = ({ id, href }: { id: number; href: Href }) => {
  const { styles } = useStyles(stylesheet);
  const router = useRouter();

  return (
    <Pressable
      hitSlop={8}
      onPress={() => {
        router.push(href);
      }}
    >
      <Text type="bodyRegular" style={styles.idLinkText}>
        {id}
      </Text>
    </Pressable>
  );
};

/** Two-line "plate over VIN" table cell. */
export const PlateVinCell = ({
  plate,
  vin,
}: {
  plate: string;
  vin: string;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.plateVinCell}>
      <Text type="caption1">{plate}</Text>

      <Text type="caption1" style={styles.secondaryText}>
        {vin}
      </Text>
    </View>
  );
};

export const PickupTypeChip = ({ isDelivery }: { isDelivery: boolean }) => (
  <Chip
    type={isDelivery ? ChipType.BRAND : ChipType.DEFAULT}
    text={isDelivery ? 'Delivery' : 'Pickup'}
  />
);

export const SubscriptionStatusChip = ({
  status,
}: {
  status: PartnerSubscriptionStatus;
}) => (
  <Chip
    type={getPartnerSubscriptionStatusChipType(status)}
    icon={<Dot />}
    text={getPartnerSubscriptionStatusLabel(status)}
    iconWidth={8}
    iconHeight={8}
  />
);

const stylesheet = createStyleSheet((theme) => ({
  idLinkText: {
    textDecorationLine: 'underline',
  },
  plateVinCell: {
    gap: 8,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
}));
